import {Path} from '../arl/index.js'
import {convert} from '../util/index.js'
export const ProfileHandling = {

// reads the source profile file and parses it into documentation
async handleSourceProfile() {

    // read the source profile file
    const rawSourceProfile = await this.readSourceProfile()

    // check
    if (! rawSourceProfile) return;

    // parse to extract the juicy bits
    this.sourceProfile = this.parseSourceProfile(rawSourceProfile)
    this.sourceProfileOrigin = 'file'

    // ok
    // console.log('** SourceProfile **', this.sourceProfile)
},

// Reads the source profile of the model
async readSourceProfile() {

    // get the model arl
    const arl = this.getArl()

    // get the full path
    const fullPath = arl?.getFullPath()
    
    // check
    if (!fullPath) return null

    // make an arl 
    const sourceProfileArl = arl.resolve(Path.removeExt(fullPath) + '.src.prf')

    // get the file
    return await sourceProfileArl.get('json')
},

/**
 * Parses the documentation entries produced by extractHandlersFromFile.
 * Organizes them per node with pin-to-handler mappings.
 *
 * @param {Array<{node: string, handlers: Array}>} docEntries
 * @returns {Map<string, Map<string, object>>} Map of nodeName -> Map of pinName -> handler metadata
 */
parseSourceProfile(raw) {

    // check
    if (!raw.entries) return null;

    const nodeMap = new Map();

    // for all nodes in the file
    for (const nodeEntry of raw.entries) {

        // get the handlers and the transmissions in that node
        const { node, handles, transmits } = nodeEntry;

        // Check if there is already an entry in the map for that node
        if (!nodeMap.has(node)) {
            nodeMap.set(node, {handles: new Map(), transmits: new Map()});
        }

        // get the list of pins
        const pinMap = nodeMap.get(node);

        // set the handlers for the pin - note that we will index the pin data using the *handler* name !
        for (const handlerData of handles) {

            const { handler, ...meta } = handlerData;
            pinMap.handles.set(handler, {handler,...meta});
        }

        // set the transmissions on the pin - here we use the *pin name* to index !!!
        for (const transmission of transmits) {

            // deconstruct
            const { pin, ...meta } = transmission;

            // get the entry (if any)
            const entry = pinMap.transmits.get(pin)

            // check if we have an entry already
            if (entry) 
                Array.isArray(entry) ? entry.push({pin,...meta}) : pinMap.transmits.set(pin, [entry, {pin,...meta}])
            else 
                pinMap.transmits.set(pin, {pin, ...meta})
        }
    }

    return nodeMap;
},

/**
 * Optional helper to flatten the nested map into a plain array (useful for UI).
 */
flattenSourceProfile(nodeMap) {
    const flatList = [];
    for (const [node, pins] of nodeMap.entries()) {
        for (const [pin, meta] of pins.entries()) {
        flatList.push({ node, pin, ...meta });
        }
    }
    return flatList;
},

getDirectInputPinProfile(pin) {

    // Get the info about the handlers of the node
    const handles = this.sourceProfile?.get(pin.node.name)?.handles

    // check
    if (!handles) return null

    // us the handler name to index the map
    const handlerName = convert.pinToHandler(pin.name)

    return handles.get(handlerName) ?? null
},

getDirectOutputPinProfile(pin) {

    // Get the info about the node
    const transmits = this.sourceProfile?.get(pin.node.name)?.transmits

    //check
    if (!transmits) return null

    return transmits.get(pin.name) ?? null
},

getProxyPinProfile(pin) {

    // proxy pins on group nodes resolve to one or more internal endpoints via the pad
    if (!pin?.is?.proxy || !pin.pad) return null

    const targets = []
    pin.pad.makeConxList(targets)

    // keep the list stable and remove duplicates caused by multiple internal routes
    const unique = new Map()
    for (const target of targets) {
        if (!target?.node?.name || !target?.name) continue
        const key = `${target.node.name}|${target.name}|${target.is.input ? 'in' : 'out'}`
        if (!unique.has(key)) unique.set(key, target)
    }

    const resolved = Array.from(unique.values())
        .map(target => ({
            node: target.node.name,
            pin: target.name,
            io: target.is.input ? 'in' : 'out',
            profile: target.is.input
                ? this.getDirectInputPinProfile(target)
                : this.getDirectOutputPinProfile(target)
        }))
        .sort((a, b) =>
            a.node.localeCompare(b.node)
            || a.pin.localeCompare(b.pin)
            || a.io.localeCompare(b.io)
        )

    return { targets: resolved }
},

// get the information for a given pin
getInputPinProfile(pin) {
    if (pin?.is?.proxy) return this.getProxyPinProfile(pin)
    return this.getDirectInputPinProfile(pin)
},

// get the information for a given pin
getOutputPinProfile(pin) {
    if (pin?.is?.proxy) return this.getProxyPinProfile(pin)
    return this.getDirectOutputPinProfile(pin)
},

getContract(pin) {

    // check
    if (!pin?.contract) return null

    // normalize types
    const types = typeof this.vmbluTypes === 'string' 
        ? JSON.parse(this.vmbluTypes) 
        : (this.vmbluTypes ?? null)

    const role = pin.contract.owner ? 'owner' : 'follower'
    const payload = pin.contract.payload ?? 'any'

    // render a vmbluType into structured lines
    const renderType = (typeName, indentLevel = 0, stack = new Set()) => {
        const indent = '  '.repeat(indentLevel)
        const lines = []
        const tokens = []
        const pushLine = (parts) => {
            const text = indent + parts.map(p => p.text).join('')
            lines.push(text)
            tokens.push({ indent: indentLevel, parts })
        }

        // no type means any
        if (!typeName) {
            pushLine([{ kind: 'type', text: 'any' }])
            return { lines, tokens, kind: 'primitive' }
        }

        // unknown or primitive
        if (!types || !types[typeName]) {
            pushLine([{ kind: 'type', text: typeName }])
            return { lines, tokens, kind: 'primitive' }
        }

        const def = types[typeName]
        const kind = def.kind ?? (def.fields ? 'object' : def.items ? 'array' : def.external ? 'external' : 'primitive')
        const summary = def.summary ? ` - ${def.summary}` : ''
        pushLine([
            { kind: 'type', text: typeName },
            { kind: 'kind', text: ` (${kind})` },
            ...(summary ? [{ kind: 'summary', text: summary }] : [])
        ])

        if (kind === 'object') {
            const required = new Set(def.required ?? [])
            const fields = def.fields ?? {}
            for (const [name, field] of Object.entries(fields)) {
                const fieldType = field.vmbluType ?? 'any'
                const fieldSummary = field.summary ? ` - ${field.summary}` : ''
                const mark = required.has(name) ? '*' : ''
                tokens.push({
                    indent: indentLevel + 1,
                    parts: [
                        { kind: 'field', text: `${name}${mark}` },
                        { kind: 'punct', text: ': ' },
                        { kind: 'type', text: fieldType },
                        ...(fieldSummary ? [{ kind: 'summary', text: fieldSummary }] : [])
                    ]
                })
                lines.push(`${indent}  ${name}${mark}: ${fieldType}${fieldSummary}`)

                if (types[fieldType] && !stack.has(fieldType)) {
                    stack.add(fieldType)
                    const child = renderType(fieldType, indentLevel + 2, stack)
                    stack.delete(fieldType)
                    lines.push(...child.lines)
                    tokens.push(...child.tokens)
                }
            }
        }
        else if (kind === 'array') {
            const itemType = def.items?.vmbluType ?? 'any'
            const itemSummary = def.items?.summary ? ` - ${def.items.summary}` : ''
            tokens.push({
                indent: indentLevel + 1,
                parts: [
                    { kind: 'field', text: 'items' },
                    { kind: 'punct', text: ': ' },
                    { kind: 'type', text: itemType },
                    ...(itemSummary ? [{ kind: 'summary', text: itemSummary }] : [])
                ]
            })
            lines.push(`${indent}  items: ${itemType}${itemSummary}`)

            if (types[itemType] && !stack.has(itemType)) {
                stack.add(itemType)
                const child = renderType(itemType, indentLevel + 2, stack)
                stack.delete(itemType)
                lines.push(...child.lines)
                tokens.push(...child.tokens)
            }
        }
        else if (kind === 'external') {
            const symbol = def.external?.symbol
            const library = def.external?.library
            if (symbol) {
                const full = library ? `${library}.${symbol}` : symbol
                tokens.push({
                    indent: indentLevel + 1,
                    parts: [
                        { kind: 'field', text: 'symbol' },
                        { kind: 'punct', text: ': ' },
                        { kind: 'type', text: full }
                    ]
                })
                lines.push(`${indent}  symbol: ${full}`)
            }
        }

        return { lines, tokens, kind, summary: def.summary ?? null }
    }

    // payload can be a single type or a request/reply pair
    if (payload && typeof payload === 'object') {
        const requestType = payload.request ?? 'any'
        const replyType = payload.reply ?? 'any'
        const request = renderType(requestType)
        const reply = renderType(replyType)

        const lines = [
            'request:',
            ...request.lines.map(line => `  ${line}`),
            'reply:',
            ...reply.lines.map(line => `  ${line}`)
        ]
        const shift = (tokens, by) => tokens.map(t => ({ ...t, indent: t.indent + by }))
        const tokens = [
            { indent: 0, parts: [{ kind: 'header', text: 'request' }] },
            ...shift(request.tokens, 1),
            { indent: 0, parts: [{ kind: 'header', text: 'reply' }] },
            ...shift(reply.tokens, 1)
        ]

        return {
            role,
            payload,
            request: { type: requestType, ...request, text: request.lines.join('\n') },
            reply: { type: replyType, ...reply, text: reply.lines.join('\n') },
            lines,
            tokens,
            text: lines.join('\n')
        }
    }

    const rendered = renderType(payload)
    return {
        role,
        payload,
        type: payload,
        ...rendered,
        tokens: rendered.tokens,
        text: rendered.lines.join('\n')
    }
}

}
